/**
 * copy_trades.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin copy trade management — session-gated, full sidebar, two tabs:
 *   1. Copy Relationships — all copier ↔ trader links
 *   2. Copy Traders       — users registered as copy traders
 * Route: /admin/copy-trades
 *
 * Tables used:
 *   copy_traders        (id, user_id → users, bio, win_rate, total_trades, followers, status, created_at)
 *   copy_relationships  (id, copier_id → users, trader_id → copy_traders, allocation_pct, status, created_at)
 *   copy_trades         (copy_rel_id → copy_relationships, symbol, direction, volume, open_price, close_price, profit_loss, status, created_at)
 *   users               (id, first_name, last_name, email)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut,
  Search, RefreshCw, Plus, X, AlertCircle,
  CheckCircle2, XCircle, Trash2, Eye,CreditCard, MapPin,
  ChevronUp, ChevronDown, TrendingUp, Users, ShieldOff, Shield,
} from "lucide-react";

const PER_PAGE = 12;

// ─── Sidebar nav ─────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users    },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet   },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt  },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
  {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },

    ],
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
    --transition-smooth: cubic-bezier(0.4, 0, 0.2, 1);
  }
  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; scroll-behavior: smooth; }

  /* Shell */
  .ct-shell { display: flex; height: 100vh; overflow: hidden; background: var(--bg-base); position: relative; }
  .ct-shell::before {
    content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* Sidebar */
  .ad-sidebar {
    width: var(--sidebar-w); flex-shrink: 0; display: flex; flex-direction: column;
    background: var(--bg-surface); border-right: 1px solid var(--border);
    position: relative; z-index: 10;
    height: 100vh; overflow: hidden;
  }
  .ad-sidebar::after {
    content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }
  .ad-logo { display: flex; align-items: center; gap: 11px; padding: 0 18px; height: var(--topbar-h); border-bottom: 1px solid var(--border); flex-shrink: 0; }
  .ad-logo-icon {
    width: 32px; height: 32px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35);
  }
  .ad-logo-text strong { font-size: 13.5px; font-weight: 800; letter-spacing: -0.4px; background: linear-gradient(to right, #eeeeff, #a5b4fc); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
  .ad-logo-text span { font-size: 9.5px; color: var(--text-muted); display: block; margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500; }
  .ad-nav {
    flex: 1; overflow-y: auto; overflow-x: hidden;
    padding: 18px 10px; display: flex; flex-direction: column; gap: 22px;
    scroll-behavior: smooth; -webkit-overflow-scrolling: touch;
    min-height: 0;
  }
  .ad-nav::-webkit-scrollbar { width: 3px; }
  .ad-nav::-webkit-scrollbar-track { background: transparent; }
  .ad-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.2); border-radius: 99px; }
  .ad-nav::-webkit-scrollbar-thumb:hover { background: rgba(99,102,241,0.4); }
  .ad-nav-section-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 10px; margin-bottom: 5px; }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link {
    display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: var(--radius-sm);
    font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    text-decoration: none; transition: all 0.18s ease; position: relative;
  }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%);
    color: var(--accent-light); font-weight: 600; box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15);
  }
  .ad-nav-link.active::before {
    content: ''; position: absolute; left: 0; top: 18%; bottom: 18%; width: 2.5px; border-radius: 99px;
    background: linear-gradient(to bottom, var(--accent), var(--purple));
    box-shadow: 0 0 10px var(--accent-glow);
  }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }
  .ad-user-card { flex-shrink: 0; border-top: 1px solid var(--border); padding: 12px 10px; background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%); }
  .ad-user-inner { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: var(--radius-sm); transition: background 0.15s; }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2)); border: 1.5px solid rgba(99,102,241,0.35); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: var(--accent-light); flex-shrink: 0; }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn { width: 28px; height: 28px; border-radius: 7px; background: none; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-muted); transition: all 0.15s; }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); }

  /* Page */
  .ct-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; }

  /* Topbar */
  .ct-topbar {
    flex-shrink: 0; height: var(--topbar-h); background: rgba(10,10,22,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; position: relative;
  }
  .ct-topbar::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px; background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%); }
  .ct-topbar-left { display: flex; align-items: center; gap: 14px; }
  .ct-topbar-badge { display: flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px; background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2); font-size: 10px; font-weight: 700; color: var(--accent-light); letter-spacing: 0.5px; text-transform: uppercase; }
  .ct-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .ct-topbar-sub { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
  .ct-topbar-right { display: flex; align-items: center; gap: 8px; }

  /* Buttons */
  .ct-btn { display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: var(--radius-sm); font-size: 11.5px; font-weight: 500; cursor: pointer; transition: all 0.18s; font-family: var(--font-sans); border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary); }
  .ct-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }
  .ct-btn-primary { display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: var(--radius-sm); font-size: 11.5px; font-weight: 600; cursor: pointer; transition: all 0.18s; font-family: var(--font-sans); border: none; background: var(--accent); color: #fff; }
  .ct-btn-primary:hover { background: #4f52e0; box-shadow: 0 0 20px rgba(99,102,241,0.35); }

  /* Tabs */
  .ct-tabs { display: flex; align-items: center; gap: 2px; padding: 0 28px; border-bottom: 1px solid var(--border); background: var(--bg-surface); flex-shrink: 0; }
  .ct-tab {
    padding: 12px 16px; font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    border: none; background: none; cursor: pointer; font-family: var(--font-sans);
    position: relative; transition: color 0.15s; display: flex; align-items: center; gap: 7px;
  }
  .ct-tab:hover { color: var(--text-primary); }
  .ct-tab.active { color: var(--accent-light); font-weight: 600; }
  .ct-tab.active::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 2px; background: var(--accent); border-radius: 99px 99px 0 0; }
  .ct-tab-pill { padding: 1px 7px; border-radius: 99px; font-size: 9.5px; font-family: var(--font-mono); background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-muted); }
  .ct-tab.active .ct-tab-pill { background: var(--accent-dim); border-color: rgba(99,102,241,0.25); color: var(--accent-light); }

  /* Main */
  .ct-main { flex: 1; overflow-y: auto; padding: 26px 28px; display: flex; flex-direction: column; gap: 20px; }
  .ct-main::-webkit-scrollbar { width: 4px; }
  .ct-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat grid */
  .ct-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
  .ct-stat-grid-5 { grid-template-columns: repeat(5, 1fr); }
  .ct-stat { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 18px 20px; display: flex; flex-direction: column; gap: 12px; position: relative; overflow: hidden; transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s; }
  .ct-stat::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%); pointer-events: none; }
  .ct-stat::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%); pointer-events: none; }
  .ct-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .ct-stat-top { display: flex; align-items: flex-start; justify-content: space-between; }
  .ct-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .ct-stat-icon { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .ct-stat-value { font-size: 22px; font-weight: 700; color: var(--text-primary); letter-spacing: -1px; font-family: var(--font-mono); line-height: 1; }
  .ct-stat-sub { font-size: 10.5px; color: var(--text-muted); margin-top: 4px; }

  /* Panel */
  .ct-panel { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .ct-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 20px; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
  .ct-toolbar-left { display: flex; align-items: center; gap: 10px; }
  .ct-toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .ct-panel-title { font-size: 13px; font-weight: 700; color: var(--text-primary); }
  .ct-count-pill { padding: 2px 8px; border-radius: 99px; border: 1px solid var(--border-hi); font-size: 10px; color: var(--text-muted); font-family: var(--font-mono); }

  /* Search */
  .ct-search-wrap { position: relative; }
  .ct-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .ct-search { padding: 6px 12px 6px 30px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary); font-family: var(--font-sans); width: 210px; outline: none; transition: border-color 0.18s; }
  .ct-search::placeholder { color: var(--text-muted); }
  .ct-search:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .ct-table-wrap { overflow-x: auto; }
  .ct-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .ct-table thead tr { border-bottom: 1px solid var(--border); }
  .ct-table th { padding: 10px 18px; text-align: left; font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); white-space: nowrap; cursor: pointer; user-select: none; transition: color 0.15s; }
  .ct-table th:hover { color: var(--text-secondary); }
  .ct-table th.no-sort { cursor: default; }
  .ct-table tbody tr { border-bottom: 1px solid rgba(255,255,255,0.035); transition: background 0.12s; }
  .ct-table tbody tr:last-child { border-bottom: none; }
  .ct-table tbody tr:hover { background: rgba(99,102,241,0.04); }
  .ct-table td { padding: 13px 18px; vertical-align: middle; }

  /* Cells */
  .ct-avatar-cell { display: flex; align-items: center; gap: 10px; }
  .ct-avatar { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
  .ct-avatar-indigo { background: rgba(99,102,241,0.18); color: #818cf8; border: 1px solid rgba(99,102,241,0.25); }
  .ct-avatar-green  { background: rgba(52,211,153,0.15);  color: #34d399; border: 1px solid rgba(52,211,153,0.2);  }
  .ct-avatar-amber  { background: rgba(251,191,36,0.15);  color: #fbbf24; border: 1px solid rgba(251,191,36,0.2);  }
  .ct-avatar-rose   { background: rgba(251,113,133,0.15); color: #fb7185; border: 1px solid rgba(251,113,133,0.2); }
  .ct-avatar-purple { background: rgba(167,139,250,0.15); color: #a78bfa; border: 1px solid rgba(167,139,250,0.2); }
  .ct-cell-name { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .ct-cell-sub  { font-size: 11px; color: var(--text-secondary); margin-top: 1px; }
  .ct-cell-date { font-size: 11.5px; color: var(--text-secondary); font-family: var(--font-mono); }
  .ct-cell-mono { font-size: 12px; color: var(--text-secondary); font-family: var(--font-mono); }

  /* Chips */
  .ct-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 99px; font-size: 10px; font-weight: 600; letter-spacing: 0.3px; }
  .ct-chip.active     { background: var(--green-dim);  color: var(--green);  border: 1px solid rgba(52,211,153,0.2); }
  .ct-chip.paused     { background: var(--amber-dim);  color: var(--amber);  border: 1px solid rgba(251,191,36,0.2); }
  .ct-chip.suspended  { background: var(--rose-dim);   color: var(--rose);   border: 1px solid rgba(251,113,133,0.2); }
  .ct-chip.inactive   { background: rgba(120,120,160,0.1); color: var(--text-secondary); border: 1px solid var(--border); }
  .ct-alloc { padding: 2px 8px; border-radius: var(--radius-sm); background: var(--bg-elevated); border: 1px solid var(--border-hi); font-size: 11px; font-weight: 600; color: var(--text-secondary); font-family: var(--font-mono); }

  /* Actions */
  .ct-actions { display: flex; align-items: center; gap: 4px; justify-content: flex-end; }
  .ct-icon-btn { width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border); background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-secondary); transition: all 0.15s; }
  .ct-icon-btn:hover { background: var(--bg-elevated); color: var(--text-primary); border-color: var(--border-hi); }
  .ct-icon-btn.danger:hover  { background: var(--rose-dim);  color: var(--rose);  border-color: rgba(251,113,133,0.3); }
  .ct-icon-btn.warning:hover { background: var(--amber-dim); color: var(--amber); border-color: rgba(251,191,36,0.3); }
  .ct-icon-btn.success:hover { background: var(--green-dim); color: var(--green); border-color: rgba(52,211,153,0.3); }

  /* Skeleton */
  .ct-skel { height: 12px; border-radius: 4px; background: rgba(255,255,255,0.05); animation: ct-pulse 1.4s ease-in-out infinite; }
  @keyframes ct-pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }

  /* Empty */
  .ct-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 48px; color: var(--text-muted); }
  .ct-empty span { font-size: 12.5px; }

  /* Pagination */
  .ct-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; border-top: 1px solid var(--border); }
  .ct-pag-info { font-size: 11px; color: var(--text-muted); }
  .ct-pag-pages { display: flex; gap: 4px; }
  .ct-pag-btn { width: 28px; height: 28px; border-radius: 6px; font-size: 11px; display: flex; align-items: center; justify-content: center; border: 1px solid var(--border); background: transparent; cursor: pointer; color: var(--text-secondary); transition: all 0.15s; font-family: var(--font-mono); }
  .ct-pag-btn:hover { background: var(--bg-elevated); color: var(--text-primary); }
  .ct-pag-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Modals */
  .ct-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 20px; }
  .ct-modal { background: var(--bg-elevated); border: 1px solid var(--border-hi); border-radius: var(--radius-lg); width: 100%; max-width: 480px; display: flex; flex-direction: column; box-shadow: 0 24px 80px rgba(0,0,0,0.6); }
  .ct-modal-wide  { max-width: 680px; }
  .ct-modal-wider { max-width: 560px; }
  .ct-modal-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px 24px 18px; border-bottom: 1px solid var(--border); }
  .ct-modal-title { font-size: 14px; font-weight: 700; color: var(--text-primary); }
  .ct-modal-sub   { font-size: 11px; color: var(--text-muted); margin-top: 3px; }
  .ct-close-btn { width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border); background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-secondary); transition: all 0.15s; flex-shrink: 0; }
  .ct-close-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ct-modal-body { padding: 20px 24px; overflow-y: auto; max-height: 60vh; display: flex; flex-direction: column; gap: 14px; }
  .ct-modal-foot { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 14px 24px; border-top: 1px solid var(--border); }

  /* Form */
  .ct-label { font-size: 11px; font-weight: 600; color: var(--text-secondary); display: block; margin-bottom: 6px; letter-spacing: 0.4px; text-transform: uppercase; }
  .ct-select-full, .ct-input { width: 100%; padding: 9px 12px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 12.5px; color: var(--text-primary); font-family: var(--font-sans); outline: none; transition: border-color 0.18s; }
  .ct-select-full:focus, .ct-input:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .ct-input::placeholder { color: var(--text-muted); }
  .ct-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .ct-error { display: flex; align-items: center; gap: 8px; padding: 10px 13px; background: var(--rose-dim); border: 1px solid rgba(251,113,133,0.2); border-radius: var(--radius-sm); font-size: 12px; color: var(--rose); }
  .ct-ghost-btn { padding: 7px 16px; border-radius: var(--radius-sm); font-size: 12px; font-weight: 500; cursor: pointer; border: 1px solid var(--border); background: transparent; color: var(--text-secondary); font-family: var(--font-sans); transition: all 0.15s; }
  .ct-ghost-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ct-submit-btn { display: flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: var(--radius-sm); font-size: 12px; font-weight: 600; cursor: pointer; border: none; background: var(--accent); color: #fff; font-family: var(--font-sans); transition: all 0.18s; }
  .ct-submit-btn:hover:not(:disabled) { background: #4f52e0; box-shadow: 0 0 20px rgba(99,102,241,0.35); }
  .ct-submit-btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .ct-danger-btn { display: flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: var(--radius-sm); font-size: 12px; font-weight: 600; cursor: pointer; border: none; background: var(--rose); color: #fff; font-family: var(--font-sans); transition: all 0.18s; }
  .ct-danger-btn:hover:not(:disabled) { background: #e5536a; }
  .ct-danger-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  /* Trader picker in AddRelModal */
  .ct-trader-search-wrap { position: relative; margin-bottom: 8px; }
  .ct-trader-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .ct-trader-search { width: 100%; padding: 7px 12px 7px 30px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary); font-family: var(--font-sans); outline: none; transition: border-color 0.18s; }
  .ct-trader-search::placeholder { color: var(--text-muted); }
  .ct-trader-search:focus { border-color: rgba(99,102,241,0.4); }
  .ct-trader-wrap { border: 1px solid var(--border); border-radius: var(--radius-sm); overflow: hidden; max-height: 230px; overflow-y: auto; }
  .ct-trader-wrap::-webkit-scrollbar { width: 3px; }
  .ct-trader-wrap::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }
  .ct-trader-row { display: flex; align-items: center; gap: 12px; padding: 10px 14px; border-bottom: 1px solid var(--border); cursor: pointer; transition: background 0.12s; position: relative; }
  .ct-trader-row:last-child { border-bottom: none; }
  .ct-trader-row:hover { background: var(--bg-hover); }
  .ct-trader-row.selected { background: var(--accent-dim); }
  .ct-trader-row.selected::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2.5px; background: var(--accent); border-radius: 0 2px 2px 0; }
  .ct-trader-row-info { flex: 1; min-width: 0; }
  .ct-trader-row-name { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .ct-trader-row-sub  { font-size: 11px; color: var(--text-secondary); margin-top: 2px; }
  .ct-trader-row-id { font-size: 10px; font-family: var(--font-mono); font-weight: 600; padding: 2px 7px; border-radius: 5px; background: var(--bg-elevated); color: var(--text-muted); border: 1px solid var(--border); flex-shrink: 0; }
  .ct-trader-row.selected .ct-trader-row-id { background: rgba(99,102,241,0.18); color: var(--accent-light); border-color: rgba(99,102,241,0.25); }
  .ct-radio { width: 15px; height: 15px; border-radius: 50%; border: 1.5px solid var(--border-hi); flex-shrink: 0; display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
  .ct-trader-row.selected .ct-radio { border-color: var(--accent); background: var(--accent); }
  .ct-radio-dot { width: 6px; height: 6px; border-radius: 50%; background: #fff; opacity: 0; transform: scale(0); transition: all 0.15s; }
  .ct-trader-row.selected .ct-radio-dot { opacity: 1; transform: scale(1); }
  .ct-trader-empty { padding: 28px; text-align: center; font-size: 12px; color: var(--text-muted); }

  /* Trade detail table */
  .ct-trade-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .ct-trade-table th { padding: 8px 14px; font-size: 9px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); white-space: nowrap; border-bottom: 1px solid var(--border); }
  .ct-trade-table td { padding: 11px 14px; border-bottom: 1px solid rgba(255,255,255,0.03); vertical-align: middle; }
  .ct-trade-table tbody tr:last-child td { border-bottom: none; }
  .ct-trade-table tbody tr:hover { background: rgba(99,102,241,0.03); }
  .ct-pnl-pos { color: var(--green); font-weight: 700; font-family: var(--font-mono); }
  .ct-pnl-neg { color: var(--rose);  font-weight: 700; font-family: var(--font-mono); }
  .ct-dir-buy  { color: var(--green); font-size: 10px; font-weight: 700; letter-spacing: 0.5px; }
  .ct-dir-sell { color: var(--rose);  font-size: 10px; font-weight: 700; letter-spacing: 0.5px; }
  .ct-trade-chip { display: inline-block; padding: 2px 8px; border-radius: 99px; font-size: 9.5px; font-weight: 600; letter-spacing: 0.3px; }
  .ct-trade-chip.open   { background: var(--amber-dim); color: var(--amber); border: 1px solid rgba(251,191,36,0.2); }
  .ct-trade-chip.closed { background: rgba(120,120,160,0.1); color: var(--text-secondary); border: 1px solid var(--border); }
  .ct-trade-chip.copy   { background: var(--accent-dim); color: var(--accent-light); border: 1px solid rgba(99,102,241,0.2); }

  /* Toast */
  .ct-toast { position: fixed; bottom: 24px; right: 24px; z-index: 100; display: flex; align-items: center; gap: 10px; padding: 12px 18px; border-radius: var(--radius-md); background: var(--bg-elevated); border: 1px solid var(--border-hi); box-shadow: 0 8px 32px rgba(0,0,0,0.5); font-size: 13px; color: var(--text-primary); animation: ct-slide-in 0.2s ease; }
  .ct-toast.ok  { border-color: rgba(52,211,153,0.35); }
  .ct-toast.err { border-color: rgba(251,113,133,0.35); }
  @keyframes ct-slide-in { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }

  /* Gate */
  .ct-gate { height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px; background: var(--bg-base); }

  /* ═══════════════════════════════════════
     MOBILE SIDEBAR OVERLAY + HAMBURGER
  ════════════════════════════════════════ */
  .ad-sidebar-overlay {
    display: none;
    position: fixed; inset: 0; background: rgba(0,0,0,0.6);
    z-index: 40; backdrop-filter: blur(2px);
    animation: overlay-fade-in 0.22s var(--transition-smooth);
  }
  @keyframes overlay-fade-in { from { opacity:0 } to { opacity:1 } }

  .ct-hamburger {
    display: none;
    width: 36px; height: 36px; border-radius: var(--radius-sm);
    background: var(--bg-card); border: 1px solid var(--border-hi);
    color: var(--text-secondary); cursor: pointer;
    align-items: center; justify-content: center;
    transition: all 0.15s; flex-shrink: 0;
  }
  .ct-hamburger:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* ═══════════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ════════════════════════════════════════ */

  /* ── Large desktop (≥1400px) ── */
  @media (min-width: 1400px) {
    .ct-stat-grid { gap: 18px; }
    .ct-main { padding: 32px 36px; }
    .ct-topbar { padding: 0 36px; }
    .ct-tabs { padding: 0 36px; }
    .ct-stat-value { font-size: 26px; }
  }

  /* ── Medium desktop (1024px – 1279px) ── */
  @media (max-width: 1279px) {
    :root { --sidebar-w: 210px; }
    .ct-main { padding: 22px 22px; }
    .ct-topbar { padding: 0 22px; }
    .ct-tabs { padding: 0 22px; }
    .ct-stat-grid { gap: 12px; }
  }

  /* ── Tablet landscape (768px – 1023px): icon-strip sidebar ── */
  @media (max-width: 1023px) {
    :root { --sidebar-w: 60px; }

    .ad-logo-text,
    .ad-nav-section-label,
    .ad-nav-link span,
    .ad-nav-chevron,
    .ad-user-info,
    .ad-logout-btn { display: none !important; }

    .ad-logo { justify-content: center; padding: 0; }
    .ad-logo-icon { width: 36px; height: 36px; }
    .ad-nav { padding: 14px 6px; gap: 14px; }
    .ad-nav-items { gap: 2px; }
    .ad-nav-link { justify-content: center; padding: 10px; gap: 0; }
    .ad-nav-link svg { opacity: 0.7; }
    .ad-nav-link.active svg { opacity: 1; }
    .ad-nav-link.active::before { top: 10%; bottom: 10%; }
    .ad-user-card { padding: 10px 6px; }
    .ad-user-inner { justify-content: center; padding: 8px; gap: 0; }

    .ct-stat-grid { grid-template-columns: repeat(2, 1fr); }
    .ct-main { padding: 18px 18px; gap: 16px; }
    .ct-topbar { padding: 0 18px; }
    .ct-tabs { padding: 0 18px; }
    .ct-toolbar { flex-direction: column; align-items: stretch; gap: 10px; }
    .ct-toolbar-right { flex-wrap: wrap; }
    .ct-search { width: 100%; }
  }

  /* ── Mobile (≤767px): off-canvas drawer ── */
  @media (max-width: 767px) {
    :root { --sidebar-w: 232px; }

    .ct-hamburger { display: flex; }

    .ad-sidebar {
      position: fixed; top: 0; left: 0; height: 100vh;
      z-index: 50; transform: translateX(-100%);
      transition: transform 0.28s var(--transition-smooth);
      box-shadow: 4px 0 40px rgba(0,0,0,0.5);
      overflow: hidden;
    }
    .ad-sidebar.open { transform: translateX(0); }
    .ad-sidebar-overlay.open { display: block; }

    /* Restore all hidden items inside the open mobile drawer */
    .ad-sidebar.open .ad-logo-text,
    .ad-sidebar.open .ad-nav-section-label,
    .ad-sidebar.open .ad-nav-link span,
    .ad-sidebar.open .ad-nav-chevron,
    .ad-sidebar.open .ad-user-info,
    .ad-sidebar.open .ad-logout-btn { display: initial !important; }

    .ad-sidebar.open .ad-sidebar-close { display: flex !important; }
    .ad-sidebar.open .ad-logo { justify-content: flex-start; padding: 0 18px; }
    .ad-sidebar.open .ad-nav { padding: 18px 10px; gap: 22px; }
    .ad-sidebar.open .ad-nav-link { justify-content: flex-start; padding: 8px 10px; gap: 9px; }
    .ad-sidebar.open .ad-nav-link span { display: block !important; flex: 1; }
    .ad-sidebar.open .ad-nav-section-label { display: block !important; }
    .ad-sidebar.open .ad-user-inner { justify-content: flex-start; gap: 10px; padding: 9px 10px; }

    .ct-shell { overflow: hidden; }
    .ct-page { width: 100%; }

    .ct-stat-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .ct-stat-value { font-size: 18px; }
    .ct-stat { padding: 14px; }
    .ct-stat-icon { width: 28px; height: 28px; }

    .ct-main { padding: 14px 12px; gap: 14px; }
    .ct-topbar { padding: 0 14px; }
    .ct-topbar-sub { display: none; }
    .ct-topbar-title { font-size: 13px; }
    .ct-topbar-badge { display: none; }
    .ct-tabs { padding: 0 14px; }

    .ct-toolbar { padding: 10px 14px; flex-direction: column; align-items: stretch; gap: 10px; }
    .ct-toolbar-right { flex-direction: column; align-items: stretch; gap: 8px; }
    .ct-search { width: 100%; }

    /* Hide less critical columns on mobile — keep copier, trader, status, actions */
    .ct-table th:nth-child(3), .ct-table td:nth-child(3),
    .ct-table th:nth-child(4), .ct-table td:nth-child(4) { display: none; }
    .ct-cell-sub { display: none; }
    .ct-table th, .ct-table td { padding: 10px 10px; }

    .ct-overlay { align-items: flex-end; padding: 0; }
    .ct-modal, .ct-modal-wide, .ct-modal-wider { border-radius: var(--radius-md) var(--radius-md) 0 0; max-width: 100%; }
    .ct-modal-foot { flex-wrap: wrap; gap: 8px; }
    .ct-two-col { grid-template-columns: 1fr; }

    .ct-pagination { flex-direction: column; align-items: flex-start; gap: 10px; padding: 10px 14px; }
    .ct-pag-pages { flex-wrap: wrap; }

    .ct-toast { bottom: 16px; right: 12px; left: 12px; font-size: 12px; }
  }

  /* ── Small mobile (≤480px) ── */
  @media (max-width: 480px) {
    .ct-stat-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .ct-stat-value { font-size: 16px; }
    .ct-stat-sub { font-size: 9px; }
    .ct-stat { padding: 12px; gap: 8px; }

    .ct-avatar { width: 26px; height: 26px; font-size: 9px; }
    .ct-avatar-cell { gap: 7px; }
    .ct-cell-name { font-size: 12px; }

    /* On very small screens collapse tab labels */
    .ct-tab { padding: 10px 10px; font-size: 11.5px; gap: 5px; }
  }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(str)     { if (!str) return "—"; return new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
function fmtDateTime(str) { if (!str) return "—"; return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
function fmtCurrency(val) { if (val == null) return "—"; return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(val); }
function initials(first, last) { return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?"; }
const PALETTE = ["indigo", "green", "amber", "rose", "purple"];
function avatarColor(id) {
  const n = typeof id === "string" ? id.charCodeAt(0) + (id.charCodeAt(id.length - 1) || 0) : (id ?? 0);
  return PALETTE[n % PALETTE.length];
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Sort icon ────────────────────────────────────────────────────────────────
function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
  return sortDir === 1
    ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
    : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
}

// ─── Trades Detail Modal ──────────────────────────────────────────────────────
function TradesModal({ rel, onClose }) {
  const [trades,  setTrades]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data } = await supabase.from("copy_trades").select("*").eq("copy_rel_id", rel.id).order("opened_at", { ascending: false });
      setTrades(data ?? []);
      setLoading(false);
    }
    load();
  }, [rel.id]);

  const copierName = `${rel.users?.first_name ?? ""} ${rel.users?.last_name ?? ""}`.trim() || "—";
  const traderName = rel.trader?.display_name ?? rel.trader?.handle ?? "—";

  return (
    <div className="ct-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal ct-modal-wide">
        <div className="ct-modal-head">
          <div>
            <div className="ct-modal-title">Copy trades</div>
            <div className="ct-modal-sub">{copierName} copying {traderName}</div>
          </div>
          <button className="ct-close-btn" onClick={onClose}><X size={13} /></button>
        </div>
        <div className="ct-modal-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 10 }}>
              {Array.from({ length: 5 }).map((_, i) => <div key={i} className="ct-skel" style={{ height: 36 }} />)}
            </div>
          ) : trades.length === 0 ? (
            <div className="ct-empty"><AlertCircle size={20} /><span>No copy trades for this relationship</span></div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="ct-trade-table">
                <thead>
                  <tr>{["Symbol","Direction","Volume","Open Price","Close Price","P&L","Status","Date"].map((h) => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {trades.map((t) => {
                    const pnl = t.profit_loss ?? 0;
                    return (
                      <tr key={t.id}>
                        <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>{t.symbol ?? "—"}</td>
                        <td><span className={t.trade_type === "buy" ? "ct-dir-buy" : "ct-dir-sell"}>{t.trade_type?.toUpperCase() ?? "—"}</span></td>
                        <td style={{ color: "var(--text-secondary)" }}>{t.lots ?? "—"}</td>
                        <td style={{ color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>{t.open_price ?? "—"}</td>
                        <td style={{ color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>{t.close_price ?? "—"}</td>
                        <td><span className={pnl >= 0 ? "ct-pnl-pos" : "ct-pnl-neg"}>{pnl >= 0 ? "+" : ""}{fmtCurrency(pnl)}</span></td>
                        <td><span className={`ct-trade-chip ${t.status ?? "copy"}`}>{t.status ?? "—"}</span></td>
                        <td style={{ color: "var(--text-muted)", fontSize: 11 }}>{fmtDate(t.opened_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="ct-modal-foot" style={{ justifyContent: "space-between" }}>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{trades.length} trade{trades.length !== 1 ? "s" : ""} total</span>
          <button className="ct-ghost-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Relationship Modal ───────────────────────────────────────────────────
function AddRelModal({ onClose, onSuccess }) {
  const [users,        setUsers]        = useState([]);
  const [copyTraders,  setCopyTraders]  = useState([]);
  const [copierId,     setCopierId]     = useState("");
  const [traderId,     setTraderId]     = useState("");
  const [allocPct,     setAllocPct]     = useState("");
  const [traderSearch, setTraderSearch] = useState("");
  const [loading,      setLoading]      = useState(false);
  const [fetching,     setFetching]     = useState(true);
  const [error,        setError]        = useState("");

  useEffect(() => {
    async function load() {
      setFetching(true);
      const [{ data: u }, { data: ct }] = await Promise.all([
        supabase.from("users").select("id, first_name, last_name, email").order("first_name"),
        supabase.from("trader_profiles")
          .select("id, user_id, display_name, handle, market, risk_level, is_verified, is_active, total_copiers")
          .eq("is_active", true)
          .order("display_name"),
      ]);
      setUsers(u ?? []);
      setCopyTraders(ct ?? []);
      setFetching(false);
    }
    load();
  }, []);

  const filteredTraders = useMemo(() => {
    const q = traderSearch.toLowerCase();
    if (!q) return copyTraders;
    return copyTraders.filter((t) => {
      const name = `${t.display_name ?? ""} ${t.handle ?? ""}`.toLowerCase();
      return name.includes(q) || String(t.id).includes(q);
    });
  }, [copyTraders, traderSearch]);

  const selectedTrader = copyTraders.find((t) => String(t.id) === String(traderId));

  async function handleSubmit() {
    setError("");
    if (!copierId || !traderId) { setError("Select both a copier and a trader."); return; }
    if (copierId === selectedTrader?.user_id?.toString()) { setError("A user cannot copy themselves."); return; }
    setLoading(true);
    try {
      const { error: err } = await supabase.from("copy_relationships").insert({
        copier_id: copierId, trader_id: traderId,
        allocated_amount: allocPct ? parseFloat(allocPct) : 0,
        status: "active",
      });
      if (err) throw err;
      onSuccess("Copy relationship created.");
      onClose();
    } catch (e) {
      setError(e.message ?? "Failed to create relationship.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ct-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal ct-modal-wider">
        <div className="ct-modal-head">
          <div><div className="ct-modal-title">Add copy relationship</div><div className="ct-modal-sub">Link a copier to a copy trader</div></div>
          <button className="ct-close-btn" onClick={onClose}><X size={13} /></button>
        </div>
        <div className="ct-modal-body">
          {fetching ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="ct-skel" style={{ height: 40 }} />) : (
            <>
              <div>
                <label className="ct-label">Copier (user) <span style={{ color: "var(--rose)" }}>*</span></label>
                <select className="ct-select-full" value={copierId} onChange={(e) => setCopierId(e.target.value)}>
                  <option value="">Select user…</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} — {u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="ct-label">
                  Trader to copy <span style={{ color: "var(--rose)" }}>*</span>
                  {selectedTrader && <span style={{ color: "var(--accent-light)", fontWeight: 500, textTransform: "none", marginLeft: 8, letterSpacing: 0 }}>— {selectedTrader.display_name}</span>}
                </label>
                <div className="ct-trader-search-wrap">
                  <Search size={12} className="ct-trader-search-icon" />
                  <input type="text" placeholder="Search by name or email…" value={traderSearch} onChange={(e) => setTraderSearch(e.target.value)} className="ct-trader-search" />
                </div>
                <div className="ct-trader-wrap">
                  {copyTraders.length === 0 ? (
                    <div className="ct-trader-empty">No active copy traders found. Add traders in the Copy Traders tab.</div>
                  ) : filteredTraders.length === 0 ? (
                    <div className="ct-trader-empty">No traders match "{traderSearch}"</div>
                  ) : filteredTraders.map((t) => {
                    const isSelected = String(t.id) === String(traderId);
                    const aColor = avatarColor(t.user_id ?? t.id);
                    return (
                      <div key={t.id} className={`ct-trader-row${isSelected ? " selected" : ""}`} onClick={() => setTraderId(String(t.id))}>
                        <div className="ct-radio"><div className="ct-radio-dot" /></div>
                        <div className={`ct-avatar ct-avatar-${aColor}`} style={{ width: 28, height: 28, fontSize: 10 }}>{t.initials || t.display_name?.[0] || "T"}</div>
                        <div className="ct-trader-row-info">
                          <div className="ct-trader-row-name">{t.display_name ?? "—"} {t.is_verified ? "✓" : ""}</div>
                          <div className="ct-trader-row-sub">@{t.handle} · {t.market ?? "—"} · {t.risk_level ?? "—"} risk · {t.total_copiers ?? 0} copiers</div>
                        </div>
                        <span className="ct-trader-row-id">#{t.id.slice(0, 6)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="ct-label">Allocated Amount <span style={{ color: "var(--text-muted)", textTransform: "none", fontWeight: 400 }}>(USD, optional)</span></label>
                <input type="number" min="0" step="0.01" value={allocPct} onChange={(e) => setAllocPct(e.target.value)} placeholder="e.g. 500" className="ct-input" />
              </div>
              {error && <div className="ct-error"><AlertCircle size={13} style={{ flexShrink: 0 }} />{error}</div>}
            </>
          )}
        </div>
        <div className="ct-modal-foot">
          <button className="ct-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="ct-submit-btn" disabled={loading || fetching || !traderId} onClick={handleSubmit}><Plus size={12} />{loading ? "Creating…" : "Create relationship"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Copy Trader Modal ────────────────────────────────────────────────────
function AddCopyTraderModal({ onClose, onSuccess }) {
  const [users,       setUsers]       = useState([]);
  const [userId,      setUserId]      = useState("");
  const [displayName, setDisplayName] = useState("");
  const [handle,      setHandle]      = useState("");
  const [market,      setMarket]      = useState("forex");
  const [riskLevel,   setRiskLevel]   = useState("medium");
  const [bio,         setBio]         = useState("");
  const [loading,     setLoading]     = useState(false);
  const [fetching,    setFetching]    = useState(true);
  const [error,       setError]       = useState("");

  useEffect(() => {
    supabase.from("users").select("id, first_name, last_name, email").order("first_name")
      .then(({ data }) => { setUsers(data ?? []); setFetching(false); });
  }, []);

  async function handleSubmit() {
    setError("");
    if (!userId)      { setError("Select a user."); return; }
    if (!displayName.trim()) { setError("Display name is required."); return; }
    if (!handle.trim())      { setError("Handle is required."); return; }
    setLoading(true);
    try {
      const { error: err } = await supabase.from("trader_profiles").insert({
        user_id:      userId,
        display_name: displayName.trim(),
        handle:       handle.trim().toLowerCase().replace(/\s+/g, "_"),
        market,
        risk_level:   riskLevel,
        bio:          bio.trim() || null,
        is_active:    true,
        is_verified:  false,
        total_copiers: 0,
        total_clients: 0,
        years_active:  0,
      });
      if (err) throw err;
      onSuccess("Copy trader added.");
      onClose();
    } catch (e) {
      setError(e.message ?? "Failed to add trader.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ct-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal">
        <div className="ct-modal-head">
          <div><div className="ct-modal-title">Add copy trader</div><div className="ct-modal-sub">Register a user as a copy trader</div></div>
          <button className="ct-close-btn" onClick={onClose}><X size={13} /></button>
        </div>
        <div className="ct-modal-body">
          {fetching ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="ct-skel" style={{ height: 40 }} />) : (
            <>
              <div>
                <label className="ct-label">User <span style={{ color: "var(--rose)" }}>*</span></label>
                <select className="ct-select-full" value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <option value="">Select user…</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} — {u.email}</option>)}
                </select>
              </div>
              <div className="ct-two-col">
                <div>
                  <label className="ct-label">Display name <span style={{ color: "var(--rose)" }}>*</span></label>
                  <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Alpha Trader" className="ct-input" />
                </div>
                <div>
                  <label className="ct-label">Handle <span style={{ color: "var(--rose)" }}>*</span></label>
                  <input type="text" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="e.g. alpha_trader" className="ct-input" />
                </div>
              </div>
              <div className="ct-two-col">
                <div>
                  <label className="ct-label">Market</label>
                  <select className="ct-select-full" value={market} onChange={(e) => setMarket(e.target.value)}>
                    <option value="forex">Forex</option>
                    <option value="crypto">Crypto</option>
                    <option value="stocks">Stocks</option>
                    <option value="commodities">Commodities</option>
                    <option value="indices">Indices</option>
                  </select>
                </div>
                <div>
                  <label className="ct-label">Risk level</label>
                  <select className="ct-select-full" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="ct-label">Bio <span style={{ color: "var(--text-muted)", textTransform: "none", fontWeight: 400 }}>(optional)</span></label>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Short trader bio…" rows={3} className="ct-input" style={{ resize: "vertical", lineHeight: 1.5 }} />
              </div>
              {error && <div className="ct-error"><AlertCircle size={13} style={{ flexShrink: 0 }} />{error}</div>}
            </>
          )}
        </div>
        <div className="ct-modal-foot">
          <button className="ct-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="ct-submit-btn" disabled={loading || fetching || !userId} onClick={handleSubmit}><Plus size={12} />{loading ? "Adding…" : "Add trader"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Modal ────────────────────────────────────────────────────────────
function ConfirmModal({ title = "Confirm", message, onConfirm, onClose, loading, danger = true }) {
  return (
    <div className="ct-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal" style={{ maxWidth: 400 }}>
        <div className="ct-modal-head">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: danger ? "var(--rose-dim)" : "var(--amber-dim)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Trash2 size={15} style={{ color: danger ? "var(--rose)" : "var(--amber)" }} />
            </div>
            <div className="ct-modal-title">{title}</div>
          </div>
          <button className="ct-close-btn" onClick={onClose}><X size={13} /></button>
        </div>
        <div className="ct-modal-body">
          <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>{message}</p>
        </div>
        <div className="ct-modal-foot">
          <button className="ct-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="ct-danger-btn" disabled={loading} onClick={onConfirm}><Trash2 size={12} />{loading ? "Removing…" : "Remove"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CopyTrades() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [sessionReady,  setSessionReady]  = useState(false);
  const [activeTab,     setActiveTab]     = useState("relationships"); // "relationships" | "traders"

  // ── Relationships state ───────────────────────────────────────────────────
  const [rels,       setRels]       = useState([]);
  const [relsLoading,setRelsLoading]= useState(true);
  const [relSearch,  setRelSearch]  = useState("");
  const [relSort,    setRelSort]    = useState({ field: "created_at", dir: -1 });
  const [relPage,    setRelPage]    = useState(1);
  const [showAddRel, setShowAddRel] = useState(false);
  const [viewRel,    setViewRel]    = useState(null);
  const [deleteRel,  setDeleteRel]  = useState(null);
  const [deletingRel,setDeletingRel]= useState(false);

  // ── Copy traders state ────────────────────────────────────────────────────
  const [copyTraders,    setCopyTraders]    = useState([]);
  const [tradersLoading, setTradersLoading] = useState(true);
  const [traderSearch,   setTraderSearch]   = useState("");
  const [traderSort,     setTraderSort]     = useState({ field: "created_at", dir: -1 });
  const [traderPage,     setTraderPage]     = useState(1);
  const [showAddTrader,  setShowAddTrader]  = useState(false);
  const [deleteTrader,   setDeleteTrader]   = useState(null);
  const [deletingTrader, setDeletingTrader] = useState(false);

  const [toast, setToast] = useState({ msg: "", ok: true });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── Session ───────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  function showToastMsg(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3200);
  }

  // ── Fetch relationships ───────────────────────────────────────────────────
  const fetchRels = useCallback(async () => {
    setRelsLoading(true);
    const { data } = await supabase
      .from("copy_relationships")
      .select(`
        id, status, allocated_amount, copy_ratio, stop_loss_pct, realised_gain, started_at,
        users!copy_relationships_copier_id_fkey ( id, first_name, last_name, email ),
        trader:trader_profiles (
          id, display_name, handle, market, risk_level, is_verified, total_copiers
        )
      `)
      .order("started_at", { ascending: false });
    setRels(data ?? []);
    setRelsLoading(false);
  }, []);

  // ── Fetch copy traders ────────────────────────────────────────────────────
  const fetchCopyTraders = useCallback(async () => {
    setTradersLoading(true);
    const { data } = await supabase
      .from("trader_profiles")
      .select("id, user_id, display_name, handle, market, risk_level, is_verified, is_active, total_copiers, total_clients, badge_label, badge_type, years_active, status, created_at, first_name, last_name, email, bio")
      .order("created_at", { ascending: false });
    setCopyTraders(data ?? []);
    setTradersLoading(false);
  }, []);

  useEffect(() => {
    if (!sessionReady) return;
    fetchRels();
    fetchCopyTraders();
  }, [sessionReady, fetchRels, fetchCopyTraders]);

  // ── Delete relationship ───────────────────────────────────────────────────
  async function handleDeleteRel() {
    if (!deleteRel) return;
    setDeletingRel(true);
    try {
      await supabase.from("copy_trades").delete().eq("copy_rel_id", deleteRel.id);
      const { error } = await supabase.from("copy_relationships").delete().eq("id", deleteRel.id);
      if (error) throw error;
      showToastMsg("Copy relationship removed.");
      setDeleteRel(null);
      await fetchRels();
    } catch (e) {
      showToastMsg("Error: " + (e.message ?? "Failed to remove."), false);
    } finally {
      setDeletingRel(false);
    }
  }

  // ── Suspend / reactivate trader ───────────────────────────────────────────
  async function toggleTraderStatus(trader) {
    const next = !trader.is_active;
    const { error } = await supabase.from("trader_profiles").update({ is_active: next }).eq("id", trader.id);
    if (error) { showToastMsg("Failed to update status.", false); return; }
    showToastMsg(`Trader ${next ? "reactivated" : "deactivated"}.`);
    setCopyTraders((prev) => prev.map((t) => t.id === trader.id ? { ...t, is_active: next } : t));
  }

  // ── Delete trader ─────────────────────────────────────────────────────────
  async function handleDeleteTrader() {
    if (!deleteTrader) return;
    setDeletingTrader(true);
    try {
      const { error } = await supabase.from("trader_profiles").delete().eq("id", deleteTrader.id);
      if (error) throw error;
      showToastMsg("Copy trader removed.");
      setDeleteTrader(null);
      await fetchCopyTraders();
    } catch (e) {
      showToastMsg("Error: " + (e.message ?? "Failed to remove."), false);
    } finally {
      setDeletingTrader(false);
    }
  }

  // ── Relationship filter + sort ────────────────────────────────────────────
  const filteredRels = useMemo(() => {
    const q = relSearch.toLowerCase();
    let list = rels.filter((r) => {
      const copierName = `${r.users?.first_name ?? ""} ${r.users?.last_name ?? ""}`.toLowerCase();
      const traderName = (r.trader?.display_name ?? r.trader?.handle ?? "").toLowerCase();
      return !q || copierName.includes(q) || traderName.includes(q) || r.users?.email?.toLowerCase().includes(q);
    });
    list.sort((a, b) => {
      if (relSort.field === "copier") return `${a.users?.first_name}${a.users?.last_name}`.localeCompare(`${b.users?.first_name}${b.users?.last_name}`) * relSort.dir;
      if (relSort.field === "trader") return (a.trader?.display_name ?? "").localeCompare(b.trader?.display_name ?? "") * relSort.dir;
      return (new Date(a.started_at ?? 0) - new Date(b.started_at ?? 0)) * relSort.dir;
    });
    return list;
  }, [rels, relSearch, relSort]);

  const relTotalPages = Math.ceil(filteredRels.length / PER_PAGE);
  const relPageSlice  = filteredRels.slice((relPage - 1) * PER_PAGE, relPage * PER_PAGE);
  function handleRelSort(field) { setRelSort((s) => ({ field, dir: s.field === field ? s.dir * -1 : -1 })); setRelPage(1); }

  // ── Copy traders filter + sort ────────────────────────────────────────────
  const filteredTraders = useMemo(() => {
    const q = traderSearch.toLowerCase();
    let list = copyTraders.filter((t) => {
      const name  = `${t.display_name ?? ""} ${t.handle ?? ""} ${t.first_name ?? ""} ${t.last_name ?? ""}`.toLowerCase();
      const email = (t.email ?? "").toLowerCase();
      return !q || name.includes(q) || email.includes(q);
    });
    list.sort((a, b) => {
      if (traderSort.field === "name")          return (a.display_name ?? "").localeCompare(b.display_name ?? "") * traderSort.dir;
      if (traderSort.field === "total_copiers") return ((a.total_copiers ?? 0) - (b.total_copiers ?? 0)) * traderSort.dir;
      if (traderSort.field === "market")        return (a.market ?? "").localeCompare(b.market ?? "") * traderSort.dir;
      if (traderSort.field === "risk_level")    return (a.risk_level ?? "").localeCompare(b.risk_level ?? "") * traderSort.dir;
      return (new Date(a.created_at ?? 0) - new Date(b.created_at ?? 0)) * traderSort.dir;
    });
    return list;
  }, [copyTraders, traderSearch, traderSort]);

  const traderTotalPages = Math.ceil(filteredTraders.length / PER_PAGE);
  const traderPageSlice  = filteredTraders.slice((traderPage - 1) * PER_PAGE, traderPage * PER_PAGE);
  function handleTraderSort(field) { setTraderSort((s) => ({ field, dir: s.field === field ? s.dir * -1 : -1 })); setTraderPage(1); }

  // ── Stats ─────────────────────────────────────────────────────────────────
  const relStats = useMemo(() => ({
    total:         rels.length,
    active:        rels.filter((r) => r.status === "active").length,
    uniqueCopiers: new Set(rels.map((r) => r.users?.id)).size,
    uniqueTraders: new Set(rels.map((r) => r.trader?.id)).size,
  }), [rels]);

  const traderStats = useMemo(() => ({
    total:        copyTraders.length,
    active:       copyTraders.filter((t) => t.is_active).length,
    suspended:    copyTraders.filter((t) => !t.is_active).length,
    totalCopiers: copyTraders.reduce((s, t) => s + (t.total_copiers ?? 0), 0),
  }), [copyTraders]);

  const REL_STAT_CARDS = [
    { label: "Total relationships", value: relStats.total,         icon: Copy,      bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)",  bar: "var(--accent)",  sub: "All time" },
    { label: "Active",             value: relStats.active,         icon: TrendingUp,bg: "rgba(52,211,153,0.15)",  color: "var(--green)",        glow: "rgba(52,211,153,0.08)",  bar: "var(--green)",   sub: "Currently copying" },
    { label: "Unique copiers",     value: relStats.uniqueCopiers,  icon: Users,     bg: "rgba(167,139,250,0.15)", color: "var(--purple)",       glow: "rgba(167,139,250,0.08)", bar: "var(--purple)",  sub: "Distinct users" },
    { label: "Unique traders",     value: relStats.uniqueTraders,  icon: UserCheck, bg: "rgba(34,211,238,0.15)",  color: "var(--cyan)",         glow: "rgba(34,211,238,0.08)",  bar: "var(--cyan)",    sub: "Being copied" },
  ];

  const TRADER_STAT_CARDS = [
    { label: "Total traders",   value: traderStats.total,        icon: UserCheck, bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)",  bar: "var(--accent)"  },
    { label: "Active",          value: traderStats.active,       icon: TrendingUp,bg: "rgba(52,211,153,0.15)",  color: "var(--green)",        glow: "rgba(52,211,153,0.08)",  bar: "var(--green)"   },
    { label: "Inactive",        value: traderStats.suspended,    icon: ShieldOff, bg: "rgba(251,113,133,0.15)", color: "var(--rose)",         glow: "rgba(251,113,133,0.08)", bar: "var(--rose)"    },
    { label: "Total copiers",   value: traderStats.totalCopiers, icon: Shield,    bg: "rgba(34,211,238,0.15)",  color: "var(--cyan)",         glow: "rgba(34,211,238,0.08)",  bar: "var(--cyan)",   sub: "Across all traders" },
  ];

  const REL_COLS    = [{ key: "copier", label: "Copier" }, { key: "trader", label: "Trader" }, { key: "created_at", label: "Since" }];
  const TRADER_COLS = [{ key: "name", label: "Trader" }, { key: "market", label: "Market" }, { key: "risk_level", label: "Risk" }, { key: "total_copiers", label: "Copiers" }, { key: "created_at", label: "Joined" }];

  if (!sessionReady) {
    return (
      <>
        <StyleInjector />
        <div className="ct-gate">
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#6366f1,#a78bfa)", boxShadow: "0 0 24px rgba(99,102,241,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Copy size={18} color="#fff" />
          </div>
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Verifying session…</span>
        </div>
      </>
    );
  }

  return (
    <div className="ct-shell">
      <StyleInjector />

      {/* ── Mobile sidebar overlay ── */}
      <div
        className={`ad-sidebar-overlay${sidebarOpen ? " open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Sidebar ───────────────────────────────────────────────────── */}
      <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon"><Copy size={16} color="#fff" /></div>
          <div className="ad-logo-text"><strong>Tradexflow</strong><span>Admin Panel</span></div>
          <button
            className="ad-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            style={{
              marginLeft: "auto", display: "none",
              width: 28, height: 28, borderRadius: 7,
              background: "none", border: "1px solid var(--border)",
              color: "var(--text-muted)", cursor: "pointer",
              alignItems: "center", justifyContent: "center",
              flexShrink: 0, transition: "all 0.15s",
            }}
          >
            <X size={13} />
          </button>
        </div>
        <nav className="ad-nav" onClick={() => setSidebarOpen(false)}>
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <div className="ad-nav-section-label">{section.label}</div>
              <div className="ad-nav-items">{section.items.map((item) => <SidebarLink key={item.to} {...item} />)}</div>
            </div>
          ))}
        </nav>
        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info"><strong>Administrator</strong><span>{adminEmail}</span></div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Sign out"><LogOut size={13} /></button>
          </div>
        </div>
      </aside>

      {/* ── Page column ───────────────────────────────────────────────── */}
      <div className="ct-page">

        {/* Topbar */}
        <header className="ct-topbar">
          <div className="ct-topbar-left">
            <button className="ct-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="3" width="14" height="1.5" rx="0.75" fill="currentColor"/>
                <rect x="1" y="7.25" width="14" height="1.5" rx="0.75" fill="currentColor"/>
                <rect x="1" y="11.5" width="10" height="1.5" rx="0.75" fill="currentColor"/>
              </svg>
            </button>
            <div className="ct-topbar-badge"><Copy size={10} />Copy Trades</div>
            <div>
              <div className="ct-topbar-title">Copy trading</div>
              <div className="ct-topbar-sub">Manage copy traders and their relationships</div>
            </div>
          </div>
          <div className="ct-topbar-right">
            <button className="ct-btn" onClick={() => { fetchRels(); fetchCopyTraders(); }}><RefreshCw size={12} />Refresh</button>
            {activeTab === "relationships"
              ? <button className="ct-btn-primary" onClick={() => setShowAddRel(true)}><Plus size={12} />Add relationship</button>
              : <button className="ct-btn-primary" onClick={() => setShowAddTrader(true)}><Plus size={12} />Add trader</button>
            }
          </div>
        </header>

        {/* Tabs */}
        <div className="ct-tabs">
          <button className={`ct-tab${activeTab === "relationships" ? " active" : ""}`} onClick={() => setActiveTab("relationships")}>
            Copy Relationships <span className="ct-tab-pill">{rels.length}</span>
          </button>
          <button className={`ct-tab${activeTab === "traders" ? " active" : ""}`} onClick={() => setActiveTab("traders")}>
            Copy Traders <span className="ct-tab-pill">{copyTraders.length}</span>
          </button>
        </div>

        {/* Main */}
        <main className="ct-main">

          {/* ── RELATIONSHIPS TAB ── */}
          {activeTab === "relationships" && (
            <>
              <div className="ct-stat-grid">
                {REL_STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
                  <div key={label} className="ct-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                    <div className="ct-stat-top">
                      <span className="ct-stat-label">{label}</span>
                      <div className="ct-stat-icon" style={{ background: bg }}><Icon size={15} style={{ color }} /></div>
                    </div>
                    <div><div className="ct-stat-value">{value}</div>{sub && <div className="ct-stat-sub">{sub}</div>}</div>
                  </div>
                ))}
              </div>

              <div className="ct-panel">
                <div className="ct-toolbar">
                  <div className="ct-toolbar-left">
                    <span className="ct-panel-title">All relationships</span>
                    <span className="ct-count-pill">{filteredRels.length}</span>
                  </div>
                  <div className="ct-toolbar-right">
                    <div className="ct-search-wrap">
                      <Search size={12} className="ct-search-icon" />
                      <input type="text" placeholder="Search by name or email…" value={relSearch} onChange={(e) => { setRelSearch(e.target.value); setRelPage(1); }} className="ct-search" />
                    </div>
                  </div>
                </div>

                <div className="ct-table-wrap">
                  <table className="ct-table">
                    <thead>
                      <tr>
                        {REL_COLS.map(({ key, label }) => (
                          <th key={key} onClick={() => handleRelSort(key)}>{label}<SortIcon field={key} sortField={relSort.field} sortDir={relSort.dir} /></th>
                        ))}
                        <th className="no-sort">Allocation</th>
                        <th className="no-sort">Status</th>
                        <th className="no-sort" style={{ textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relsLoading ? (
                        Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i}>{Array.from({ length: 6 }).map((_, j) => <td key={j}><div className="ct-skel" style={{ width: `${45 + (i * j * 11) % 40}%` }} /></td>)}</tr>
                        ))
                      ) : relPageSlice.length === 0 ? (
                        <tr><td colSpan={6}><div className="ct-empty"><AlertCircle size={22} /><span>No copy relationships found</span></div></td></tr>
                      ) : relPageSlice.map((r, i) => {
                        const copier = r.users;
                        const trader = r.trader;
                        return (
                          <tr key={r.id}>
                            <td>
                              <div className="ct-avatar-cell">
                                <div className={`ct-avatar ct-avatar-${avatarColor(copier?.id ?? i)}`}>{initials(copier?.first_name, copier?.last_name)}</div>
                                <div><div className="ct-cell-name">{copier?.first_name} {copier?.last_name}</div><div className="ct-cell-sub">{copier?.email}</div></div>
                              </div>
                            </td>
                            <td>
                              <div className="ct-avatar-cell">
                                <div className={`ct-avatar ct-avatar-${avatarColor(trader?.id ?? i + 2)}`} style={{ fontFamily: "var(--font-mono)", fontSize: 10 }}>{(trader?.initials || trader?.display_name?.[0] || "T").toUpperCase()}</div>
                                <div>
                                  <div className="ct-cell-name">{trader?.display_name ?? "—"}</div>
                                  <div className="ct-cell-sub">@{trader?.handle ?? "—"} · {trader?.market ?? "—"}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className="ct-cell-date">{fmtDateTime(r.started_at)}</span></td>
                            <td>{r.allocated_amount != null ? <span className="ct-alloc">${r.allocated_amount}</span> : <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>}</td>
                            <td><span className={`ct-chip ${r.status ?? "inactive"}`}>{r.status ?? "—"}</span></td>
                            <td>
                              <div className="ct-actions">
                                <button className="ct-icon-btn" onClick={() => setViewRel(r)} title="View trades"><Eye size={12} /></button>
                                <button className="ct-icon-btn danger" onClick={() => setDeleteRel(r)} title="Remove"><Trash2 size={12} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {relTotalPages > 1 && (
                  <div className="ct-pagination">
                    <span className="ct-pag-info">{filteredRels.length ? `${(relPage - 1) * PER_PAGE + 1}–${Math.min(relPage * PER_PAGE, filteredRels.length)} of ${filteredRels.length}` : "0 results"}</span>
                    <div className="ct-pag-pages">
                      {Array.from({ length: Math.min(relTotalPages, 8) }, (_, i) => i + 1).map((p) => (
                        <button key={p} className={`ct-pag-btn${relPage === p ? " active" : ""}`} onClick={() => setRelPage(p)}>{p}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── COPY TRADERS TAB ── */}
          {activeTab === "traders" && (
            <>
              <div className="ct-stat-grid">
                {TRADER_STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
                  <div key={label} className="ct-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                    <div className="ct-stat-top">
                      <span className="ct-stat-label">{label}</span>
                      <div className="ct-stat-icon" style={{ background: bg }}><Icon size={15} style={{ color }} /></div>
                    </div>
                    <div><div className="ct-stat-value">{value}</div>{sub && <div className="ct-stat-sub">{sub}</div>}</div>
                  </div>
                ))}
              </div>

              <div className="ct-panel">
                <div className="ct-toolbar">
                  <div className="ct-toolbar-left">
                    <span className="ct-panel-title">All copy traders</span>
                    <span className="ct-count-pill">{filteredTraders.length}</span>
                  </div>
                  <div className="ct-toolbar-right">
                    <div className="ct-search-wrap">
                      <Search size={12} className="ct-search-icon" />
                      <input type="text" placeholder="Search by name or email…" value={traderSearch} onChange={(e) => { setTraderSearch(e.target.value); setTraderPage(1); }} className="ct-search" />
                    </div>
                  </div>
                </div>

                <div className="ct-table-wrap">
                  <table className="ct-table">
                    <thead>
                      <tr>
                        {TRADER_COLS.map(({ key, label }) => (
                          <th key={key} onClick={() => handleTraderSort(key)}>{label}<SortIcon field={key} sortField={traderSort.field} sortDir={traderSort.dir} /></th>
                        ))}
                        <th className="no-sort">Status</th>
                        <th className="no-sort" style={{ textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradersLoading ? (
                        Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i}>{Array.from({ length: 7 }).map((_, j) => <td key={j}><div className="ct-skel" style={{ width: `${45 + (i * j * 11) % 40}%` }} /></td>)}</tr>
                        ))
                      ) : traderPageSlice.length === 0 ? (
                        <tr><td colSpan={7}><div className="ct-empty"><AlertCircle size={22} /><span>No copy traders found. Add one to get started.</span></div></td></tr>
                      ) : traderPageSlice.map((t) => {
                        const aColor = avatarColor(t.user_id ?? t.id);
                        return (
                          <tr key={t.id}>
                            <td>
                              <div className="ct-avatar-cell">
                                <div className={`ct-avatar ct-avatar-${aColor}`} style={{ fontSize: 10 }}>{t.initials || t.display_name?.[0] || "T"}</div>
                                <div>
                                  <div className="ct-cell-name" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    {t.display_name ?? (`${t.first_name ?? ""} ${t.last_name ?? ""}`.trim() || "—")}
                                    {t.is_verified && <CheckCircle2 size={11} style={{ color: "var(--green)", flexShrink: 0 }} />}
                                  </div>
                                  <div className="ct-cell-sub">@{t.handle} · {t.email ?? "—"}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className="ct-cell-mono" style={{ color: "var(--text-secondary)", textTransform: "capitalize" }}>{t.market ?? "—"}</span></td>
                            <td>
                              <span className={`ct-chip ${t.risk_level === "low" ? "active" : t.risk_level === "high" ? "inactive" : ""}`} style={{ fontSize: 10 }}>
                                {t.risk_level ?? "—"}
                              </span>
                            </td>
                            <td><span className="ct-cell-mono">{t.total_copiers ?? 0}</span></td>
                            <td><span className="ct-cell-date">{fmtDate(t.created_at)}</span></td>
                            <td><span className={`ct-chip ${t.is_active ? "active" : "inactive"}`}>{t.is_active ? "active" : "inactive"}</span></td>
                            <td>
                              <div className="ct-actions">
                                <button
                                  className={`ct-icon-btn ${t.is_active ? "warning" : "success"}`}
                                  onClick={() => toggleTraderStatus(t)}
                                  title={t.is_active ? "Deactivate" : "Reactivate"}
                                >
                                  {t.is_active ? <ShieldOff size={12} /> : <Shield size={12} />}
                                </button>
                                <button className="ct-icon-btn danger" onClick={() => setDeleteTrader(t)} title="Remove"><Trash2 size={12} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {traderTotalPages > 1 && (
                  <div className="ct-pagination">
                    <span className="ct-pag-info">{filteredTraders.length ? `${(traderPage - 1) * PER_PAGE + 1}–${Math.min(traderPage * PER_PAGE, filteredTraders.length)} of ${filteredTraders.length}` : "0 results"}</span>
                    <div className="ct-pag-pages">
                      {Array.from({ length: Math.min(traderTotalPages, 8) }, (_, i) => i + 1).map((p) => (
                        <button key={p} className={`ct-pag-btn${traderPage === p ? " active" : ""}`} onClick={() => setTraderPage(p)}>{p}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────── */}
      {showAddRel && <AddRelModal onClose={() => setShowAddRel(false)} onSuccess={(msg) => { showToastMsg(msg); fetchRels(); }} />}
      {viewRel    && <TradesModal rel={viewRel} onClose={() => setViewRel(null)} />}
      {deleteRel  && (
        <ConfirmModal
          title="Remove relationship"
          message={`Remove the copy relationship between ${deleteRel.users?.first_name} ${deleteRel.users?.last_name} and ${deleteRel.trader?.display_name ?? "this trader"}? All associated copy trades will also be deleted.`}
          onConfirm={handleDeleteRel}
          onClose={() => setDeleteRel(null)}
          loading={deletingRel}
        />
      )}
      {showAddTrader && <AddCopyTraderModal onClose={() => setShowAddTrader(false)} onSuccess={(msg) => { showToastMsg(msg); fetchCopyTraders(); }} />}
      {deleteTrader  && (
        <ConfirmModal
          title="Remove trader"
          message={`Remove ${deleteTrader.display_name ?? deleteTrader.handle} as a copy trader? This will not delete their user account.`}
          onConfirm={handleDeleteTrader}
          onClose={() => setDeleteTrader(null)}
          loading={deletingTrader}
        />
      )}

      {/* Toast */}
      {toast.msg && (
        <div className={`ct-toast ${toast.ok ? "ok" : "err"}`}>
          {toast.ok ? <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} /> : <XCircle size={14} style={{ color: "var(--rose)", flexShrink: 0 }} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}